import { EnumReactionType, EnumSecurityType, EnumUserSegment, EnumUserStatus } from "./common-enum";

export type IBaseParam = {
    language: string;
    country: string;
};

export interface IUser {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    nickName: string;
    email: string;
    isDeleted?: boolean;
    companyName?: string;
    profileLogoData?: string; // Partner Logo Base64
    status: EnumUserStatus;
    userSegment?: EnumUserSegment;
}

export interface IAutoCompleteTrader {
    id?: string;
    nickName: string;
    firstName: string | null;
    lastName: string | null;
    userStatus: EnumUserStatus;
    isProfilePictureAvailable: boolean;
    imageUrl: string;
}

interface IWikifolioLongDescription {
    isTranslationAvailable: boolean;
    content: string;
    originalContent: string;
}

export interface IWikifolio {
    id: string;
    symbol: string;
    fullName: string;
    wkn?: string | null;
    shortDescription: string;
    description: IWikifolioLongDescription;
    status: number;
    isOnWatchlist: boolean;
    intendedInvestment?: number;
    isLicensed: boolean;
    isin: string;
    chartImageUrl: string;
    dailyFee: number;
    performanceFee: number;
    containsLeverageProducts: boolean;
    currency: string;
    isParadise?: boolean;
}

export interface IWikifolioDescription {
    wikifolioId: string;
    shortDescription: string;
}

export interface IApiResponse {
    isSuccess: boolean;
    error: string;
}

export interface ILabelValue {
    label: string;
    value: string;
}

export interface ITitleAndDescription {
    title: string;
    description: string;
}

export interface ILink {
    url: string;
    name?: string | null;
    /**
     * excluded from schema validation because typing it properly as string | null creates
     * unnecessary level of complexity without adding any real benefits
     * @TJS-ignore
     */
    target?: string; // string | null
}

export interface IImage {
    url: string;
    alt: string | null;
}

export interface IUmbracoImage {
    url: string;
    alt: string | null;
    srcDisclosure: string;
    width: number;
    height: number;
}

export interface IUmbracoVideo {
    url: string;
    previewImage?: IImage;
    alt?: string | null;
}

export interface IVideo {
    url: string;
    alt?: string;
}

export interface ITooltip {
    content: string;
    link: ILink | null;
}

export interface IBroker {
    url: string;
    name: string;
    image: IImage;
}

export interface ILikeInfo {
    didLike: boolean;
    count: number;
}

interface ITeaserItemAuthor {
    image: {
        alt: string;
        url: string;
    };
    authorUrl: string;
    name: string;
}

export interface ITeaserArticle {
    image: IImage;
    title?: string | null;
    author?: ITeaserItemAuthor | null;
    description: string;
    releaseDate?: string | null;
    link: ILink;
}

export interface ITimeRangeSelectItem {
    value: number;
    label: string;
    labelLong: string;
    isDisabled?: boolean;
}

export interface IMentionedUnderlying {
    isin: string;
    shortDescription: string;
    performance: number | null;
    containedInWikifolios: number | null;
    canBeFollowed: boolean;
    isFollowed: boolean;
    securityType: EnumSecurityType;
}

export interface IVotingAnswer {
    text: string;
    count?: number;
}

export interface IAggregatedReactionItem {
    reactionType: EnumReactionType;
    numberOfReactions: number;
    positiveReactions: number;
    negativeReactions: number;
    weightedNumberOfReactions: number;
    weightedPositiveReactions: number;
    weightedNegativeReactions: number;
}

export type TAggregatedReactions = ReadonlyArray<IAggregatedReactionItem>;

export type ISortDirectionType = 0 | 1 | null;

export interface ITrader extends Pick<IUser, "id" | "firstName" | "lastName" | "nickName" | "isDeleted"> {
    displayName: string;
    traderImageUrl: string;
}

export type NonEmptyArray<T> = [T, ...T[]];

// Source: https://stackoverflow.com/questions/57103834/typescript-omit-a-property-from-all-interfaces-in-a-union-but-keep-the-union-s#answer-57103940
export type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

export enum EnumFigureType {
    Performance = 0,
    MaxLoss = 1,
    InvestedCapital = 2,
    SharpRatio = 3,
    RankingPlace = 4,
    LiquidationFigure = 5,
    TradingVolume = 6,
    Reservations = 7,
    Volatility = 8,
    Isin = 9, // text field
    Price = 10,
    PerformanceFee = 11,
    Date = 12,
}

export interface IFigure {
    creationDate?: string;
    label?: string;
    date?: string;
    id: string;
    value: string | null;
    type: EnumFigureType;
}
